/**
 * ClassificationProgressScreen — Loading screen displayed while a
 * classification request is in progress. Accepts a single typed config object
 * that determines the product card layout.
 *
 * Two classification types:
 * - SdsConfig:        barcode/ASIN flow — single product thumbnail
 * - AiResearchConfig: image capture flow — horizontal scrollable captured images
 */

import React from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import { Button, Icon, Thumbnail } from "@amzn/meridian";
import { useNavigate } from "react-router-dom";
import { ClassificationType } from "src/types/classificationTypes";
import CapturedImagesRow from "./CapturedImagesRow";
import AiStarLoader from "./AiStarLoader";
import "./classification.scss";

/* ── Config types (discriminated union) ── */

export interface SdsConfig {
  type: ClassificationType.SDS;
  asin: string;
  imageUrl?: string;
  productName?: string;
}

export interface AiResearchConfig {
  type: ClassificationType.AI_RESEARCH;
  capturedImages: string[];
}

export type ClassificationConfig = SdsConfig | AiResearchConfig;

/* ── Component ── */

interface Props {
  classificationConfig: ClassificationConfig;
}

const ClassificationProgressScreen: React.FC<Props> = ({ classificationConfig }) => {
  const navigate = useNavigate();

  return (
    <Column className="app-gradient-bg home-content pwa-scrollable" spacingInset="500" spacing="400">

      {/* Header */}
      <Row alignmentVertical="center" spacingInset="300 none none" spacing="400">
        <Button type="icon" onClick={() => navigate("/home")}>
          <Icon color="success" tokens={arrowLeftTokens} />
        </Button>
        <Column spacing="200" spacingInset="400 none">
          <Text type="h400">Analyzing Product...</Text>
          <Text type="b200" color="secondary">
            Processing product data and determining compliant waste profile
          </Text>
        </Column>
      </Row>

      {/* Product card — differs by classification type */}
      {classificationConfig.type === ClassificationType.SDS ? (
        <Row className="home-card" spacing="400" spacingInset="400" alignmentVertical="center">
          {classificationConfig.imageUrl && (
            <Thumbnail source={classificationConfig.imageUrl} size="medium" type="square" />
          )}
          <Column spacing="100">
            {classificationConfig.productName && (
              <Text type="h100">{classificationConfig.productName}</Text>
            )}
            <Text type="b100" color="secondary">ASIN: {classificationConfig.asin}</Text>
          </Column>
        </Row>
      ) : (
        <CapturedImagesRow
          images={classificationConfig.capturedImages}
          badgeLabel="ANALYZING"
        />
      )}

      {/* Loader */}
      <Row spacingInset="400 none none" alignmentVertical="center" spacing="300">
        <AiStarLoader size={40} />
        <Text type="b300" color="secondary">Fetching waste profile recommendation</Text>
      </Row>
    </Column>
  );
};

export default ClassificationProgressScreen;
